import React from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  TableBody,
  TableRow,
  TableCell,
  Button,
  IconButton,
  Tooltip,
  Avatar,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
  Stack,
} from "@mui/material";
import {
  Message as MessageIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { formatDate } from "utils/helperFunctions";

interface RootState {
  user: {
    user: {
      uid: string;
    };
  };
  requests: {
    receivedRequests: {
      [key: string]: {
        gameId: string;
        status: string;
        senderId: string;
        senderName: string;
        timeStamp: number;
      };
    };
  };
}

interface Props {
  setModalDets: (dets: { open: boolean; status: string; reqData: any }) => void;
  gameId: string;
}

const RequestsTableBody = (props: Props) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user.user);
  const receivedRequests = useSelector(
    (state: RootState) => state.requests.receivedRequests,
  );

  const renderRequestsTableData = () => {
    const { setModalDets, gameId } = props;
    if (!receivedRequests) return null;

    const reqIds = Object.keys(receivedRequests);
    const filteredRequests = reqIds
      .map((reqId) => ({ id: reqId, ...receivedRequests[reqId] }))
      .filter((req) => req.gameId === gameId && req.status === "pending")
      .sort((a, b) => b.timeStamp - a.timeStamp); // Sort by newest first

    return filteredRequests.map((reqData) => (
      <TableRow
        key={reqData.id}
        hover
        sx={{
          "&:hover": {
            backgroundColor: "action.hover",
          },
          "&:nth-of-type(odd)": {
            backgroundColor: "action.hover",
            opacity: 0.5,
          },
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <TableCell
          onClick={() => navigate(`/profile/${reqData.senderId}`)}
          sx={{
            cursor: "pointer",
            "&:hover": {
              color: "primary.main",
            },
            py: isMobile ? 1 : 2,
            px: isMobile ? 1 : 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: isMobile ? 1 : 2,
            }}
          >
            <Avatar
              sx={{
                width: isMobile ? 28 : 32,
                height: isMobile ? 28 : 32,
                bgcolor: "primary.main",
                fontSize: isMobile ? "0.75rem" : "0.875rem",
              }}
            >
              <PersonIcon fontSize={isMobile ? "small" : "medium"} />
            </Avatar>
            <Box>
              <Box
                sx={{
                  fontWeight: 500,
                  fontSize: isMobile ? "0.875rem" : "1rem",
                  mb: 0.5,
                }}
              >
                {reqData.senderName}
              </Box>
              <Chip
                label="Pending"
                size="small"
                color="warning"
                variant="outlined"
                sx={{
                  fontSize: isMobile ? "0.625rem" : "0.75rem",
                  height: isMobile ? 18 : 20,
                  "& .MuiChip-label": {
                    px: isMobile ? 0.5 : 1,
                  },
                }}
              />
              {isMobile && (
                <Box
                  sx={{
                    fontSize: "0.75rem",
                    color: "text.secondary",
                    mt: 0.5,
                  }}
                >
                  {formatDate(reqData.timeStamp)}
                </Box>
              )}
            </Box>
          </Box>
        </TableCell>

        <TableCell
          sx={{
            color: "text.secondary",
            display: { xs: "none", sm: "table-cell" },
            py: isMobile ? 1 : 2,
            px: isMobile ? 1 : 2,
          }}
        >
          {formatDate(reqData.timeStamp)}
        </TableCell>

        <TableCell sx={{ py: isMobile ? 1 : 2, px: isMobile ? 1 : 2 }}>
          <Stack
            direction={isMobile ? "column" : "row"}
            spacing={isMobile ? 0.5 : 1}
            alignItems="center"
            justifyContent="center"
          >
            <Tooltip title="Send Message">
              <IconButton
                size={isMobile ? "small" : "medium"}
                color="info"
                onClick={() => navigate(`/conversation/${reqData.senderId}`)}
                sx={{
                  bgcolor: "info.light",
                  color: "info.contrastText",
                  "&:hover": {
                    bgcolor: "info.main",
                  },
                  minWidth: isMobile ? 28 : 32,
                  minHeight: isMobile ? 28 : 32,
                }}
              >
                <MessageIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Accept Request">
              <IconButton
                size={isMobile ? "small" : "medium"}
                color="success"
                onClick={() => {
                  setModalDets({
                    open: true,
                    status: "acceptRequest",
                    reqData,
                  });
                }}
                sx={{
                  bgcolor: "success.light",
                  color: "success.contrastText",
                  "&:hover": {
                    bgcolor: "success.main",
                  },
                  minWidth: isMobile ? 28 : 32,
                  minHeight: isMobile ? 28 : 32,
                }}
              >
                <CheckIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Reject Request">
              <IconButton
                size={isMobile ? "small" : "medium"}
                color="error"
                onClick={() => {
                  setModalDets({
                    open: true,
                    status: "declineRequest",
                    reqData,
                  });
                }}
                sx={{
                  bgcolor: "error.light",
                  color: "error.contrastText",
                  "&:hover": {
                    bgcolor: "error.main",
                  },
                  minWidth: isMobile ? 28 : 32,
                  minHeight: isMobile ? 28 : 32,
                }}
              >
                <CloseIcon fontSize={isMobile ? "small" : "medium"} />
              </IconButton>
            </Tooltip>
          </Stack>
        </TableCell>
      </TableRow>
    ));
  };

  return <TableBody>{renderRequestsTableData()}</TableBody>;
};

export default RequestsTableBody;
